import { getDefinition } from "../catalog";
import { MULTIPLUS_PRIMARY_PE_MODELS } from "../catalog/charger";
import { makeRvPanel, rvPanelBranchCount, RV_PANEL_ID } from "../catalog/rv-distribution";
import { effectiveTerminals } from "../effective";
import type { ComponentDefinition, ComponentNode } from "../types";

/** Presentation edits may retain reviewed hardware identity; altered
 * electrical roles, ratings, internal wiring or switching behavior may not.
 * A custom definition can keep a catalog id after becoming a different part.
 */
export function matchesAcSafetyIdentity(node: ComponentNode, def: ComponentDefinition): boolean {
  const branches = rvPanelBranchCount(def);
  const original = branches != null && def.id === RV_PANEL_ID ? makeRvPanel(branches) : getDefinition(def.id);
  if (!original || def.category !== original.category || def.manufacturer !== original.manufacturer ||
    def.model !== original.model || def.voltage !== original.voltage ||
    def.acVoltage !== original.acVoltage || def.renderVariant !== original.renderVariant ||
    JSON.stringify(def.internalDcConnections) !== JSON.stringify(original.internalDcConnections) ||
    JSON.stringify(def.oneWayDcConnections) !== JSON.stringify(original.oneWayDcConnections)) return false;
  const stateBehavior = (definition: ComponentDefinition) => definition.simulateStates && {
    default: definition.simulateStates.default,
    options: definition.simulateStates.options.map(option => ({
      id: option.id, ampMultiplier: option.ampMultiplier, ampOverride: option.ampOverride,
      role: option.role, intraLinks: option.intraLinks,
    })),
  };
  if (JSON.stringify(stateBehavior(def)) !== JSON.stringify(stateBehavior(original))) return false;
  const terms = effectiveTerminals(def, node);
  // These drawing ground connections were added after the original catalog
  // release. A saved custom copy may omit them without changing its reviewed
  // line/neutral transfer behavior. This does not infer grounding or bonding;
  // any ground terminal that IS present must still match the reviewed entry.
  const optionalDrawingGrounds = ["shore-magnum-msh3012rv", "shore-magnum-msh3012rv-l"].includes(original.id)
    ? new Set(["ac-in-pe", "ac-out-pe", "chassis"])
    : MULTIPLUS_PRIMARY_PE_MODELS.has(original.id)
      ? new Set(["chassis"]) : new Set<string>();
  const expectedTerms = original.terminals.filter(expected =>
    !optionalDrawingGrounds.has(expected.id) || terms.some(t => t.id === expected.id));
  return terms.length === expectedTerms.length && expectedTerms.every(expected => {
    const found = terms.filter(terminal => terminal.id === expected.id);
    return found.length === 1 && found[0].polarity === expected.polarity &&
      found[0].direction === expected.direction && found[0].maxAmp === expected.maxAmp;
  });
}
